import {
  useQueries,
  useQuery,
  useQueryClient,
  type QueryClient,
  type QueryKey,
  type UseQueryResult,
} from '@tanstack/react-query'
import { useCallback, useRef, useState } from 'react'

import { accessRefused, api, unwrap } from '../api/client'
import type { Lookups, PersonName, Ref } from '../api/types'
import { heldEra, useSessionEra } from '../documents/held'
import { t } from '../i18n'

/**
 * Read once, and let go of the last answer if this one is refused.
 *
 * React Query keeps the last good answer beside a failed refetch, so a 401 or
 * 403 on a refresh left the names the instance had just declined to give still
 * in the cache and still on screen. A refusal is a verdict about who is
 * asking, and an answer this reader may no longer have is not one to go on
 * showing: it is dropped as the refusal arrives, so nothing after it -- a
 * second refusal, or a transport failure that says nothing about access -- can
 * bring it back. Only a read the instance answers does.
 *
 * A transport failure keeps the answer, with no change here: a browser that
 * could not reach the instance has learnt nothing about what it would allow.
 */
export async function readOrForget<T>(
  client: QueryClient,
  queryKey: QueryKey,
  read: () => Promise<T>,
): Promise<T> {
  try {
    return await read()
  } catch (error) {
    if (accessRefused(error)) {
      client
        .getQueryCache()
        .find({ queryKey, exact: true })
        ?.setState({ data: undefined, dataUpdatedAt: 0, status: 'pending' })
    }
    throw error
  }
}

/**
 * The project's reference lists, fetched once and reused by every form.
 *
 * They change about as often as the project does, so they are cached for the
 * length of a session rather than re-read per screen: a create form that waits
 * on six lists over a site uplink is a create form people stop opening.
 *
 * Kept under the session that asked, and that is the boundary the ten minutes
 * live inside. The lists name the project's people, and the query store
 * outlives a sign-out, so a key saying only which project would hand the next
 * person at this browser the last one's lists before any read of theirs had
 * been answered. The era is last, so that every project's lists are still one
 * prefix to invalidate.
 */
export function useLookups(projectId: string): UseQueryResult<Lookups> {
  const client = useQueryClient()
  const queryKey = useLookupsKey(projectId)
  return useQuery({
    queryKey,
    staleTime: 10 * 60 * 1000,
    queryFn: () =>
      readOrForget(client, queryKey, async () =>
        unwrap(await api.GET('/session/lookups', { params: { query: { project_id: projectId } } })),
      ),
  })
}

/**
 * The key the lists are kept under, for a reader that has to name the read.
 *
 * One definition, here, beside the read that uses it: a screen that seals the
 * lists by a key of its own spelling seals a key no read answers once this one
 * changes, and the lists then stay withheld after access comes back. A hook,
 * because the key carries the session, and so that whatever it comes to carry
 * next reaches every caller without any of them changing.
 */
export function useLookupsKey(projectId: string): readonly unknown[] {
  return lookupsKey(projectId, useSessionEra())
}

/**
 * The same key for a session named outright: for an answer that has to touch
 * the lists of the session it was asked in, and not of whichever one is here
 * when it arrives.
 */
export function lookupsKey(projectId: string, era: number): readonly unknown[] {
  return ['session', 'lookups', projectId, era]
}

/** Turn an identifier back into the name a person recognises. */
export function labelFor(refs: Ref[] | undefined, id: string | null | undefined): string {
  if (!id) return '—'
  return refs?.find((ref) => ref.id === id)?.label ?? id
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** Whether a value is shaped like an identifier, rather than being a word. */
export function looksLikeId(value: string): boolean {
  return UUID.test(value)
}

/**
 * A name for an identifier that could be anybody's, or the identifier back.
 *
 * Event summaries carry their actor as a bare identifier — every module writes
 * `summary_args["actor"] = str(person_id)` — and the sentence is assembled in
 * the client, so without this the activity feed reads
 * "0f2c… raised CM-PUN-VM-00014". Which list an argument belongs to is not
 * stated anywhere in the event, so this tries people and then companies rather
 * than making the caller guess, and hands back anything it does not recognise
 * unchanged: a person who has left the project, or an id that was never a
 * person at all, should read as what it is rather than as a blank.
 */
export function nameFor(lookups: Lookups | undefined, value: string): string {
  if (!UUID.test(value)) return value
  const hit =
    lookups?.people.find((ref) => ref.id === value) ??
    lookups?.companies.find((ref) => ref.id === value)
  return hit?.label ?? value
}

/**
 * A member's name off the lookups alone, and never the id.
 *
 * For a component that is handed the lookups and not a `nameOf`: it can name
 * the project's members and says plainly that it cannot name anybody else,
 * rather than printing the identifier it was given.
 */
export function memberName(lookups: Lookups | undefined, id: string | null | undefined): string {
  if (!id) return '—'
  return lookups?.people.find((ref) => ref.id === id)?.label ?? t('people.name.unavailable')
}

/** How many ids one `/session/names` request carries; the server refuses more. */
const NAMES_PER_REQUEST = 100

/**
 * When names were last refused, per project and session.
 *
 * A refusal answers a batch, but what it says is about the reader: this
 * person, in this project and session, may not be told these names. The same
 * person can sit in more than one batch -- a screen that named [A] and then
 * [A, B] holds an answer for [A] under a key of its own, and may still have a
 * read for [A] on the wire -- and dropping only the refused key left both: the
 * older [A] answer stayed fresh in the store for the next screen to reuse,
 * and a read for [A] sent before the refusal could land after it and name A
 * again. Which key an answer sits under cannot say whether it was given
 * before a refusal, so the scope carries the count of refusals instead, and a
 * read remembers the count it was sent at.
 *
 * The count gates answers rather than joining the key. In the key, a refusal
 * would re-ask every batch at once, the refused one included, and that one
 * would be refused again and move the count again.
 */
let refusals = 0
const lastRefused = new Map<string, number>()

const scopeOf = (projectId: string, era: number) => `${projectId} ${era}`

/** A names answer to a read sent before the scope's last refusal, which is not served. */
class RefusedSinceSent extends Error {}

/**
 * Read one batch of names, serving nothing given before the scope's last refusal.
 *
 * On a refusal, every other stored batch in the same project and session is
 * emptied and marked refused as well -- not read again, which would put the
 * question to the instance straight back -- and is read afresh the next time
 * a screen asks for it. An answer to a read sent before the refusal is not
 * served when it lands. A read sent after it and answered is served as
 * usual. A transport failure changes none of this.
 */
async function readNames(
  client: QueryClient,
  queryKey: QueryKey,
  projectId: string,
  era: number,
  read: () => Promise<PersonName[]>,
): Promise<PersonName[]> {
  const scope = scopeOf(projectId, era)
  const sentAt = refusals
  let answer: PersonName[]
  try {
    answer = await readOrForget(client, queryKey, read)
  } catch (error) {
    if (accessRefused(error)) {
      refusals += 1
      lastRefused.set(scope, refusals)
      const cache = client.getQueryCache()
      const refused = cache.find({ queryKey, exact: true })
      for (const query of cache.findAll({ queryKey: ['session', 'names', projectId] })) {
        if (query === refused || query.queryKey[4] !== era) continue
        query.setState({
          data: undefined,
          dataUpdatedAt: 0,
          error: new RefusedSinceSent(),
          errorUpdatedAt: Date.now(),
          status: 'error',
          isInvalidated: true,
        })
      }
    }
    throw error
  }
  if ((lastRefused.get(scope) ?? 0) > sentAt) throw new RefusedSinceSent()
  return answer
}

export type PersonNames = {
  /**
   * The name to show for a person id on one of this project's records.
   *
   * Never the id. A member is named from the lookups; anybody else who acted
   * on the project — a Global Admin who is on no job, somebody since taken
   * off this one — is asked of the server the first time a screen names them.
   * A person the server says has left is said to have left; one it cannot
   * name at all reads "Author unavailable". Departure is never inferred from
   * a name that did not resolve: that is how a Global Admin's own drawing came
   * to say it was issued by "somebody no longer on this project".
   */
  nameOf: (id: string | null | undefined) => string
}

/**
 * Names for every person a screen shows, members or not.
 *
 * A screen calls this once and then `nameOf` wherever it renders a person,
 * however deep in its markup, without listing the ids up front: an id that
 * the lookups do not have is noted while rendering and asked for straight
 * after, in one request for everything that render missed. Ids that have
 * been asked about keep their order, so a screen that finds one more person
 * re-asks only the last batch rather than every batch before it.
 */
export function usePersonNames(projectId: string): PersonNames {
  const client = useQueryClient()
  const lookups = useLookups(projectId)
  // Everything below is kept with the project and the session it was asked
  // in. A switch of project starts from nothing rather than asking the next
  // project about the last one's people; a new session -- the same person
  // signing in again included -- starts from nothing rather than being shown
  // names that were given to the last one.
  const era = useSessionEra()
  const [asked, setAsked] = useState<{ projectId: string; era: number; ids: readonly string[] }>({
    projectId,
    era,
    ids: [],
  })
  const ids = asked.projectId === projectId && asked.era === era ? asked.ids : []
  const missed = useRef({ projectId, era, ids: new Set<string>() })
  if (missed.current.projectId !== projectId || missed.current.era !== era) {
    missed.current = { projectId, era, ids: new Set<string>() }
  }

  const batches = Array.from({ length: Math.ceil(ids.length / NAMES_PER_REQUEST) }, (_, at) =>
    ids.slice(at * NAMES_PER_REQUEST, (at + 1) * NAMES_PER_REQUEST),
  )
  const answers = useQueries({
    queries: batches.map((batch) => {
      const queryKey = ['session', 'names', projectId, batch, era]
      return {
        queryKey,
        staleTime: 10 * 60 * 1000,
        retry: false,
        // The last batch's names while a longer one is read, and only from
        // this project in this session: a placeholder is still an answer on
        // screen, and one carried across that boundary is the leak again.
        placeholderData: (previous: PersonName[] | undefined, query?: { queryKey: QueryKey }) =>
          query?.queryKey[2] === projectId && query.queryKey[4] === era ? previous : undefined,
        queryFn: () =>
          readNames(client, queryKey, projectId, era, async (): Promise<PersonName[]> =>
            unwrap(
              await api.GET('/session/names', {
                params: { query: { project_id: projectId, id: batch } },
              }),
            ),
          ),
      }
    }),
  }) as UseQueryResult<PersonName[]>[]

  const known = new Map<string, PersonName>()
  const settled = new Set<string>()
  answers.forEach((answer, index) => {
    for (const row of Array.isArray(answer.data) ? answer.data : []) known.set(row.id, row)
    if (!answer.isPending && !answer.isPlaceholderData) {
      for (const id of batches[index] ?? []) settled.add(id)
    }
  })

  const flush = useCallback(() => {
    // Queued in one session and run in the next: what it collected belongs to
    // the one that has ended, so it is not asked about in this one.
    if (era !== heldEra()) return
    const scope = missed.current
    if (scope.projectId !== projectId || scope.era !== era) return
    const fresh = [...scope.ids]
    scope.ids.clear()
    setAsked((was) => {
      const same = was.projectId === projectId && was.era === era
      const base = same ? was.ids : []
      const adding = fresh.filter((id) => !base.includes(id))
      return adding.length === 0 && same ? was : { projectId, era, ids: [...base, ...adding] }
    })
  }, [projectId, era])

  const nameOf = (id: string | null | undefined): string => {
    if (!id) return '—'
    // Read loosely: a lookups answer without its people list is a misanswer,
    // and it should cost a screen its names rather than the screen.
    const member = (lookups.data?.people ?? []).find((ref) => ref.id === id)
    if (member) return member.label
    const named = known.get(id)
    if (named) {
      return named.left_project
        ? t('people.name.left_project', { name: named.display_name })
        : named.display_name
    }
    // Something that is not an id cannot be asked about, and asking would
    // have the server refuse the whole batch it was in.
    if (!UUID.test(id)) return t('people.name.unavailable')
    if (lookups.isPending) return t('people.name.loading')
    if (settled.has(id)) return t('people.name.unavailable')
    if (!ids.includes(id) && !missed.current.ids.has(id)) {
      // Asked after this render rather than during it: a state update while
      // rendering is one React refuses, and this is called from children too.
      if (missed.current.ids.size === 0) queueMicrotask(flush)
      missed.current.ids.add(id)
    }
    return t('people.name.loading')
  }

  return { nameOf }
}
